import { css } from 'styled-components';
import StyledVars from '@capillarytech/cap-ui-library/styled';

const { CAP_WHITE, CAP_G01, CAP_BLACK, CAP_SPACE_40, CAP_SPACE_24, CAP_SPACE_64 } = StyledVars;

export default css`
  .login-page-container {
    display: flex;
    flex-direction: row !important;
    flex-flow: row nowrap;
    height: 100vh;
    background-color: ${CAP_WHITE};
  }

  .left-section {
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
  }

  .login-banner-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .login-illustration {
    max-width: clamp(80%, 85vw, 95%);
    max-height: clamp(80%, 85vh, 95%);
    object-fit: contain;
    border-radius: clamp(6px, 1vw, 12px);
    transition: all 0.3s ease;
  }

  .right-section {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    height: 100vh;
    /* Top padding follows the form's "upper-third" anchor pattern on every
       normal viewport height. Floor of 32px protects very short viewports
       (which the @media (max-height: 500px) rule below switches to
       center-alignment, since at that point the form can't fit anyway). */
    padding: max(32px, calc(50vh - 190px)) clamp(${CAP_SPACE_24}, min(4vw, 3vh), ${CAP_SPACE_64}) 32px;
    transition: padding 0.3s ease;
    min-height: 0;
    overflow-y: auto;
  }

  /* Item 2 — short viewports (the user-reported viewport_issue case):
     the upper-third anchor formula above pins to 32px, leaving the form
     at the top with empty space below. At this height the form is
     taller than the viewport, so switch to vertical-center alignment —
     overflow is balanced top/bottom and scrollable. */
  @media (max-height: 500px) {
    .right-section {
      align-items: center;
      padding: 16px clamp(${CAP_SPACE_24}, min(4vw, 3vh), ${CAP_SPACE_64});
    }
  }

  /* CapSpin wraps the form in a block-level .ant-spin-nested-loading /
     .ant-spin-container. The wrapper ends up full-width, so .login-content
     (width:100% capped at max-width 460px) renders left-aligned inside it,
     defeating .right-section's justify-content:center. Force the wrapper
     chain to full width and center .login-content with auto margins — auto
     margins center based on the used (capped) width regardless of whether
     the wrapper is block or flex. */
  .right-section .ant-spin-nested-loading,
  .right-section .ant-spin-container {
    width: 100%;
  }

  .login-content {
    display: flex;
    flex-direction: column !important;
    flex-flow: column nowrap;
    align-items: flex-start !important;
    max-width: 460px;
    width: 100%;
    min-width: 280px;
    margin-left: auto;
    margin-right: auto;
    transition: max-width 0.3s ease, min-width 0.3s ease;
  }

  .logo-section {
    display: flex;
    height: 24px;
    width: 111px;
    margin-bottom: 32px;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .capillary-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .header-section {
    display: flex;
    flex-direction: column !important;
    flex-flow: column nowrap;
    align-items: flex-start !important;
    gap: 4px;
    margin-bottom: 24px;
    text-align: left;
    transition: all 0.3s ease;
    flex-shrink: 0;
    width: 100%;
  }

  .main-title {
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    text-align: left;
    color: #091E42;
    margin: 0;
    transition: font-size 0.3s ease;
  }

  .sign-in-label {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;
    color: #5E6C84;
    margin: 0;
    transition: font-size 0.3s ease;
  }

  .login-form {
    width: 100%;
    display: flex;
    flex-direction: column !important;
    flex-flow: column nowrap;
    gap: 24px;
    margin-top: 0;
    transition: all 0.3s ease;
    flex-shrink: 1;
    min-height: 0;
    align-items: flex-start !important;
  }

  /* Password mode: inherit the base .login-form rules (gap: 24px, margin-top: 0)
     so the Welcome-back layout shares the same vertical rhythm as the email-
     entry page. No bespoke margin overrides per row. */

  .form-field {
    width: 100%;
    min-height: 40px;
    flex-shrink: 0;
  }

  .password-field-animated {
    animation: slideDown 0.3s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
      max-height: 0;
    }
    to {
      opacity: 1;
      transform: translateY(0);
      max-height: 100px;
    }
  }

  .edit-email-icon {
    cursor: pointer;
    color: #1890ff;
    transition: color 0.3s ease;
  }

  .edit-email-icon:hover {
    color: #40a9ff;
  }

  .back-button-section {
    margin-top: 8px;
  }

  .back-button {
    color: #666;
    padding: 0;
    transition: color 0.3s ease;
  }

  .back-button:hover {
    color: #1890ff;
  }

  .email-display-section {
    width: 100%;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-top: 0;
    margin-bottom: 0;
    padding: 0;
    text-align: left;
    align-self: flex-start;
  }

  .email-display-text {
    font-size: 14px;
    color: #1a1a1a;
    font-weight: 400;
    margin: 0;
    text-align: left;
  }

  /* Item 6 — Change email is now a <CapLink>. Font/colour/hover come from
     the library theme; only override what differs from CapLink defaults. */
  .change-email-link {
    white-space: nowrap;
  }


  /* Wrapper component styling */
  .username-input,
  .password-input {
    width: 100%;
  }

  /* The password step renders two CapInputs (type="password" + type="text")
     and toggles which is visible. This hides the inactive one. */
  .password-input.is-hidden {
    display: none;
  }

  /*
   * className from CapInput props lands on the HOC wrapper div, not on the
   * antd <input>. Target descendants. Border lives on:
   *  - .ant-input-affix-wrapper (antd 6 with suffix, e.g. password eye icon)
   *  - .ant-input (antd 6 without suffix, e.g. plain email)
   */
  .username-input .ant-input-affix-wrapper,
  .password-input .ant-input-affix-wrapper,
  .username-input .ant-input,
  .password-input .ant-input {
    width: 100%;
    height: 40px;
    border: 1px solid #B3BAC5 !important;
    border-radius: 4px !important;
    box-shadow: none !important;
    outline: none !important;
    transition: all 0.3s ease !important;
  }

  .username-input .ant-input-affix-wrapper,
  .password-input .ant-input-affix-wrapper {
    padding: 0 clamp(12px, 2vw, 18px) !important;
    background: #ffffff !important;
  }

  .username-input .ant-input,
  .password-input .ant-input {
    padding: 0 clamp(12px, 2vw, 18px) !important;
    font-size: 14px !important;
    background: #ffffff !important;
  }

  /* Inner input inside affix-wrapper: strip its own border/padding */
  .username-input .ant-input-affix-wrapper .ant-input,
  .password-input .ant-input-affix-wrapper .ant-input {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    outline: none !important;
    padding: 0 !important;
    height: 100% !important;
    background: transparent !important;
  }

  .username-input .ant-input-affix-wrapper:focus,
  .username-input .ant-input-affix-wrapper-focused,
  .password-input .ant-input-affix-wrapper:focus,
  .password-input .ant-input-affix-wrapper-focused,
  .username-input .ant-input:focus,
  .password-input .ant-input:focus {
    border-color: #091E42 !important;
    box-shadow: none !important;
    outline: none !important;
  }

  /* Item 4 — error state: antd applies .ant-input-affix-wrapper-status-error
     when CapInput's errorMessage prop is set. Re-apply the red border so it
     beats our default #B3BAC5 border above. */
  .username-input .ant-input-affix-wrapper-status-error,
  .username-input .ant-input-status-error,
  .username-input .ant-input-affix-wrapper-status-error:hover,
  .username-input .ant-input-affix-wrapper-status-error.ant-input-affix-wrapper-focused,
  .password-input .ant-input-affix-wrapper-status-error,
  .password-input .ant-input-status-error,
  .password-input .ant-input-affix-wrapper-status-error:hover,
  .password-input .ant-input-affix-wrapper-status-error.ant-input-affix-wrapper-focused {
    border-color: #ff4d4f !important;
    box-shadow: none !important;
  }

  /* Item 8 — kill any inner dividers/borders from antd's affix-wrapper
     children (prefix / suffix / inner input) so no faint vertical lines
     show on either side of the password dots. */
  .username-input .ant-input-affix-wrapper .ant-input-prefix,
  .username-input .ant-input-affix-wrapper .ant-input-suffix,
  .password-input .ant-input-affix-wrapper .ant-input-prefix,
  .password-input .ant-input-affix-wrapper .ant-input-suffix {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  .password-input .ant-input-affix-wrapper .ant-input,
  .username-input .ant-input-affix-wrapper .ant-input {
    border-left: none !important;
    border-right: none !important;
  }

  .username-input .ant-input::placeholder,
  .password-input .ant-input::placeholder {
    color: #999999 !important;
  }

  .submit-section {
    width: 100%;
    margin-top: 0;
  }

  .continue-button {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    transition: all 0.3s ease;
    flex-shrink: 0;
    /* Item 3 — colours (base, hover, disabled) come from the library's
       <CapButton type="primary"> theme tokens ($CAP_PRIMARY base/hover/etc).
       Custom colour overrides removed so the designer's mocks land
       automatically when the library theme updates. */
  }

  .back-link-section {
    width: 100% !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center;
    align-self: stretch !important;
    margin-top: 0;
  }

  .back-link {
    color: #091E42;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 2px;
    transition: color 0.3s ease;
    display: inline-block;
    text-align: center;
  }

  .back-link:hover {
    color: #5E6C84;
  }

  .password-toggle-icon {
    color: #999;
    font-size: clamp(18px, 2.8vw, 22px);
    user-select: none;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .password-toggle-icon:hover {
    color: #666;
    transform: scale(1.1);
  }

  .forgot-password-section {
    width: 100%;
    text-align: right;
    display: flex;
    align-items: flex-end !important;
  }

  /* Item 7 — Forgot password is now a <CapLink>. Same library theme as
     .change-email-link so the two share font / colour automatically. */
  .forgot-password-link {
    /* no custom colour / font-size — let CapLink theme apply */
  }

  /* Responsive Design - Desktop First */
  
  /* Large desktop and zoom out (1400px and above) */
  @media (min-width: 1400px) {
    .right-section {
      padding: max(32px, calc(50vh - 190px)) ${CAP_SPACE_64} 32px;
    }

    .login-content {
      max-width: 460px;
    }
  }

  /* Medium desktop zoom adjustments (1024px to 1399px) */
  @media (min-width: 1024px) and (max-width: 1399px) {
    .login-content {
      max-width: 460px;
    }
  }

  /* Small desktop and zoom in (900px to 1023px) */
  @media (min-width: 900px) and (max-width: 1023px) {
    .login-content {
      max-width: 400px;
    }
  }

  /* Tablet + mobile (≤768px): hide the marketing panel entirely and let the
     form take full width. The earlier "stack image as top banner" treatment
     was visually awkward (image looked shrunk) — moengage-style hide is
     cleaner. */
  @media (max-width: 768px) {
    .left-section {
      display: none;
    }

    .login-content {
      max-width: 100%;
      padding: 0 ${CAP_SPACE_24};
    }
  }

  @media (max-width: 480px) {
    .login-content {
      padding: 0;
    }
  }

  /* Short viewports: do NOT shrink content. The right column already has
     overflow-y: auto, so the form keeps its real size and the user can
     scroll if the viewport can't fit it. (The earlier @media (max-height: 500px)
     rule above only switches align-items to center for layout purposes.) */

`;
